// THE ROOM LADDER, DERIVED FROM IDENTITY RATHER THAN FROM ADJACENCY.
//
// A room may follow a parent room by a fixed offset. Inside a PropertyGroup that link may CROSS
// HOTELS — a room in the annex following a room in the lead — and the backend orders a unit's room
// list HOTEL-MAJOR (the lead's rooms, then each member alphabetically), sorting a cross-hotel child
// as a root within its own hotel's slice so the hotel blocks stay intact
// (Operator::CalendarsController#hierarchy_sorted).
//
// This module used to read the ladder off ADJACENCY: "a row is a child if it has a parent id, and
// it is the last child if the NEXT row has a different parent id". That is wrong the moment the
// list spans hotels. On real data (Amsterdam's Compact-Double leading a Wembar room) the Wembar
// room rendered indented beneath the LAST room of Wembar's own parent — an offset ladder drawn
// between two rooms that have nothing to do with each other. Two hotels each holding a child of
// the same lead room broke it the other way: the connector never closed, so rows from different
// hotels read as siblings in one block.
//
// So every judgement below keys off `propertyId` + `parentRoomTypeId` — never off which row sits
// above which.
//
// WHETHER A CROSS-HOTEL CHILD IS INDENTED IS THE SURFACE'S CALL, NOT THE DATA'S. A list broken into
// hotel SECTIONS must not indent one, because the heading above it would name a hotel the room is
// not in; a FLAT list has no heading to contradict, so the ladder follows the real parent and the
// row's hotel badge says where it lives. The two answers live in two functions:
//
//   * `buildRoomTypeRowMeta` — for SECTIONED surfaces (the calendar dropdowns and grids). A row is
//     indented only when its parent is present AND in the same hotel; a cross-hotel child is a
//     top-level row wearing `followsParentElsewhere`, so the surface states the relationship in
//     words; "last child" means last within its own parent AND its own hotel.
//   * `nestRoomRows` — for a list that renders the ladder itself, with an explicit
//     `crossHotelLinks` answer. Rooms Setup passes 'nest': one flat list, one ladder, no headings.
//
// UNGROUPED IS BYTE-IDENTICAL EITHER WAY. A parent is validated to be in the same property, so
// `parentSameHotel` is always true, `nestedChildCount` always equals `childCount`, and every field
// resolves to exactly what the adjacency version produced.

/** The minimum a row must carry to be placed in the ladder. */
export interface RoomTypeTreeInput {
  id: number
  /** Owning hotel. Absent (legacy literals, onboarding drafts) ⇒ treated as one hotel. */
  propertyId?: number | null
  propertyName?: string | null
  parentRoomTypeId: number | null
  /** Backend count of rooms that follow this one, ANYWHERE in the unit. */
  childCount: number
}

export interface RoomTypeRowMeta {
  /** 0 = parent, standalone, or a child whose parent is in another hotel; 1 = indented child. */
  depth: number
  /** Has followers — rendered bold with a count badge. */
  isParent: boolean
  /** Rooms that follow this one anywhere in the unit (the backend's own count). */
  childCount: number
  /** Of those, the ones actually drawn indented beneath it here (same hotel, present in the list). */
  nestedChildCount: number
  /** Last indented child of its parent WITHIN ITS OWN HOTEL — draws the rounded connector elbow. */
  isLastChild: boolean
  /** A new group starts here — add a little breathing room above the row. */
  spacedAbove: boolean
  /**
   * This room follows a parent that is NOT indented above it — another hotel's room, or a room
   * outside the current slice. The row must say so in words; it must not be indented under an
   * unrelated neighbour.
   */
  followsParentElsewhere: boolean
  /** The hotel that parent lives in, when it is another one. Null otherwise. */
  parentPropertyName: string | null
  /**
   * This row begins a hotel block — the FIRST row of the list, or the first after a hotel boundary.
   * The first row counts deliberately: a dropdown that labels the second hotel and not the first
   * reads as "these rooms, and then some Annex rooms", which is not what the list is. Callers gate
   * on `showHotels`, so a single-hotel list renders no heading at all.
   */
  startsHotelBlock: boolean
  /** The row's own hotel, carried through so a renderer needs only the meta. */
  propertyId: number | null
  propertyName: string | null
}

/**
 * Per-row tree metadata for a room list the backend has already ordered (hotel-major, then that
 * surface's own parent/child sort inside each hotel).
 */
export function buildRoomTypeRowMeta(roomTypes: readonly RoomTypeTreeInput[]): Record<number, RoomTypeRowMeta> {
  const byId = new Map<number, RoomTypeTreeInput>()
  for (const rt of roomTypes) byId.set(rt.id, rt)

  // Same hotel + actually present ⇒ the child is drawn under the parent. `null`/`undefined`
  // property ids mean "one hotel" (an ungrouped literal), which keeps legacy callers unchanged.
  const nestedUnder = (rt: RoomTypeTreeInput): RoomTypeTreeInput | null => {
    if (rt.parentRoomTypeId == null) return null
    const parent = byId.get(rt.parentRoomTypeId)
    if (!parent) return null
    if (rt.propertyId != null && parent.propertyId != null && rt.propertyId !== parent.propertyId) return null
    return parent
  }

  const nestedCounts = new Map<number, number>()
  for (const rt of roomTypes) {
    const parent = nestedUnder(rt)
    if (parent) nestedCounts.set(parent.id, (nestedCounts.get(parent.id) ?? 0) + 1)
  }

  const meta: Record<number, RoomTypeRowMeta> = {}

  roomTypes.forEach((rt, i) => {
    const parent = nestedUnder(rt)
    const isChild = parent != null
    const isParent = rt.childCount > 0
    const next = roomTypes[i + 1]
    const prev = roomTypes[i - 1]
    const nextParent = next ? nestedUnder(next) : null
    const prevParent = prev ? nestedUnder(prev) : null

    // Last indented child of THIS parent in THIS hotel: the next row is not another child of the
    // same parent, or it is but in a different hotel (which starts its own block).
    const isLastChild =
      isChild && !(nextParent?.id === parent!.id && sameHotel(next!, rt))

    const spacedAbove = i > 0 && !isChild && (isParent || prevParent != null)

    const parentElsewhere = rt.parentRoomTypeId != null && parent == null
    const rawParent = rt.parentRoomTypeId != null ? byId.get(rt.parentRoomTypeId) : undefined

    meta[rt.id] = {
      depth: isChild ? 1 : 0,
      isParent,
      childCount: rt.childCount,
      nestedChildCount: nestedCounts.get(rt.id) ?? 0,
      isLastChild,
      spacedAbove,
      followsParentElsewhere: parentElsewhere,
      parentPropertyName: parentElsewhere ? (rawParent?.propertyName ?? null) : null,
      startsHotelBlock: i === 0 || !sameHotel(rt, prev!),
      propertyId: rt.propertyId ?? null,
      propertyName: rt.propertyName ?? null,
    }
  })

  return meta
}

function sameHotel(a: { propertyId?: number | null }, b: { propertyId?: number | null }): boolean {
  if (a.propertyId == null || b.propertyId == null) return true
  return a.propertyId === b.propertyId
}

/** The Groups half of a room-type dropdown option. */
export interface RoomTypeHotelExtras {
  hotelName: string | null
  /** Identity, so the option's hotel heading can be colour-banded like that hotel's chips. */
  hotelPropertyId: number | null
  isLeadHotel: boolean
  startsHotelBlock: boolean
  followsName: string | null
  followsPropertyName: string | null
}

/**
 * The hotel/ladder labelling for ONE room-type option, derived once so the Prices and Occupancy
 * dropdowns cannot describe the same room two ways.
 *
 * `showHotels` is the caller's answer to "does this list span hotels" (lib/reporting-scope#spansHotels)
 * — when it is false every field here is null/false and the option renders exactly as it always did.
 */
export function roomTypeHotelExtras(
  room: { propertyId?: number | null; propertyName?: string | null; parentName?: string | null },
  meta: RoomTypeRowMeta | undefined,
  opts: { showHotels: boolean; leadPropertyId?: number | null },
): RoomTypeHotelExtras {
  if (!opts.showHotels) {
    return {
      hotelName: null,
      hotelPropertyId: null,
      isLeadHotel: false,
      startsHotelBlock: false,
      followsName: null,
      followsPropertyName: null,
    }
  }

  return {
    hotelName: room.propertyName ?? null,
    hotelPropertyId: room.propertyId ?? null,
    isLeadHotel: opts.leadPropertyId != null && room.propertyId === opts.leadPropertyId,
    startsHotelBlock: meta?.startsHotelBlock ?? false,
    // Only a CROSS-HOTEL link is stated in words; a same-hotel child is already drawn indented.
    followsName: meta?.followsParentElsewhere ? room.parentName ?? null : null,
    followsPropertyName: meta?.followsParentElsewhere ? meta.parentPropertyName : null,
  }
}

export interface HotelBlock<T> {
  propertyId: number | null
  propertyName: string | null
  rows: T[]
}

/**
 * A room list split into hotel blocks, in the order the backend sent them (the lead's hotel first,
 * then each member) — never re-sorted here, because that order is the one every unit-wide surface
 * shares. A list with one hotel yields exactly one block, so a caller can render blocks
 * unconditionally and the ungrouped page looks the way it always did.
 */
export function groupRowsByHotel<T extends { propertyId?: number | null; propertyName?: string | null }>(
  rows: readonly T[],
): HotelBlock<T>[] {
  const blocks: HotelBlock<T>[] = []
  const index = new Map<number | null, HotelBlock<T>>()

  for (const row of rows) {
    const key = row.propertyId ?? null
    let block = index.get(key)
    if (!block) {
      block = { propertyId: key, propertyName: row.propertyName ?? null, rows: [] }
      index.set(key, block)
      blocks.push(block)
    }
    block.rows.push(row)
  }

  return blocks
}

/**
 * The same rows, hotel-major: each hotel's block stays in the order the list arrived in (the lead's
 * hotel first, then members), and the rows INSIDE a hotel are sorted by the caller's comparator.
 *
 * This is what a unit-wide list needs when it drops its hotel headings — without headings the only
 * thing left holding a hotel together is adjacency, and a global sort would scatter a member's six
 * rooms through eighteen. A ONE-HOTEL list is exactly `[...rows].sort(compare)`, so every ungrouped
 * surface is unchanged.
 */
export function orderWithinHotels<T extends { propertyId?: number | null; propertyName?: string | null }>(
  rows: readonly T[],
  compare: (a: T, b: T) => number,
): T[] {
  return groupRowsByHotel(rows).flatMap((block) => [...block.rows].sort(compare))
}

/** One rendered row of the ladder: the room, and whether it is drawn indented under the row above. */
export interface NestedRoomRow<T> {
  row: T
  /** 0 = root or standalone; 1 = indented child, drawn directly beneath its parent. */
  depth: number
}

/**
 * What a list does with a link whose parent lives in ANOTHER hotel. The caller has to say, because
 * the right answer is a property of the SURFACE, not of the data:
 *
 *   'nest' — a FLAT list (Rooms Setup). There are no hotel headings, so there is no boundary to
 *     cross: the child leaves its own hotel's run and is drawn indented beneath its real parent,
 *     exactly like any other child. The hotel badge on the row is what says where the room lives,
 *     and the ladder is free to say the only other thing that matters — which room it follows.
 *   'root' — a SECTIONED list (a hotel-headed dropdown, a hotel-blocked table). Indenting there
 *     would file the child under a heading naming a hotel it is not in, which is the "child
 *     indented under an unrelated parent" bug. It stays a root of its own section and the surface
 *     states the link in words instead — see `buildRoomTypeRowMeta`/`followsParentElsewhere`,
 *     which is how the calendar dropdowns and grids do precisely this.
 *
 * There is no safe default. A silent one is what makes a shared helper right for one caller and
 * wrong for the next.
 */
export type CrossHotelLinks = 'nest' | 'root'

/**
 * A flat room list turned into the render order of a ladder: every root followed immediately by the
 * children that follow it, in the order they arrived.
 *
 * Nesting keys on IDENTITY (the parent id, plus `propertyId` when the surface is sectioned), never
 * on adjacency. Adjacency is what drew an offset ladder from an Amsterdam parent to an unrelated
 * Wembar room on real data; it is never consulted here.
 *
 * `parentIdOf` is a callback because a page may be showing STAGED links the operator has not saved,
 * so the ladder reshapes as the choice is made rather than after a round trip.
 *
 * A one-hotel list nests identically under either mode — a parent is validated to be in the same
 * property — so every ungrouped surface is unchanged whatever the caller passes.
 */
export function nestRoomRows<T extends { id: number; propertyId?: number | null }>(
  rows: readonly T[],
  parentIdOf: (row: T) => number | null,
  opts: { crossHotelLinks: CrossHotelLinks },
): NestedRoomRow<T>[] {
  const byId = new Map<number, T>()
  for (const row of rows) byId.set(row.id, row)

  // The row this one hangs off — if this surface will draw it there at all. A parent that is NOT
  // in the list (filtered out, or a dangling id) is no parent here: the row falls back to a root
  // and is still rendered. A row must never vanish because of who it points at.
  const linkedParent = (row: T): T | null => {
    const parentId = parentIdOf(row)
    if (parentId == null) return null
    const parent = byId.get(parentId)
    if (!parent) return null
    if (opts.crossHotelLinks === 'root' && !sameHotel(row, parent)) return null
    return parent
  }

  // Only a ROOT collects children. The ladder is one level deep by construction (a room that is
  // itself linked cannot be picked as a parent, and the backend re-validates), so on real data this
  // changes nothing — it is what makes a chain or a cycle degrade into extra roots rather than
  // silently drop the rows hanging off one.
  const rootIds = new Set(rows.filter((row) => linkedParent(row) == null).map((row) => row.id))
  const nestedParent = (row: T): T | null => {
    const parent = linkedParent(row)
    return parent != null && rootIds.has(parent.id) ? parent : null
  }

  const out: NestedRoomRow<T>[] = []
  for (const row of rows) {
    if (nestedParent(row) != null) continue
    out.push({ row, depth: 0 })
    for (const candidate of rows) {
      if (nestedParent(candidate)?.id === row.id) out.push({ row: candidate, depth: 1 })
    }
  }
  return out
}
